import React, { useState, useEffect, useRef } from "react";
import {
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  FormControl,
  Checkbox
} from "@mui/material";
import PayphoneBox from "./PayphoneBox";
import PaypalBox from "./PaypalBox";

export default function PaymentBox({
  cartItems,
  setCartItems,
  userData,
  setUserData
}) {
  const [paymentMethod, setPaymentMethod] = useState("paypal");
  const [amountInCents, setAmountInCents] = useState(0);
  const [payphoneTxId, setPayphoneTxId] = useState("");

  const [shipping, setShipping] = useState({
    metodo: "",
    ciudad: "",
    direccion: "",
    costo: 0
  });

  const stopPolling = useRef(false);

  const newPayphoneTxId = () =>
    `ORD-${Date.now()}-${Math.random().toString(36).slice(2, 10).toUpperCase()}`;

  const calcSubtotal = () => {
    return cartItems.reduce(
      (s, it) => s + Number(it.price_with_iva ?? it.price ?? 0) * it.quantity,
      0
    );
  };

  useEffect(() => {
    const subtotal = calcSubtotal();
    const total = subtotal + shipping.costo;
    setAmountInCents(Math.round(total * 100));
  }, [cartItems, shipping]);

  useEffect(() => {
    if (paymentMethod !== "payphone") return;
    if (!payphoneTxId) return;

    const checkStatus = async () => {
      try {
        const origin = window.location.origin;
        const url = `${origin.replace(
          /\/$/,
          ""
        )}/ecommerce/api/payphone/status/?clientTransactionId=${encodeURIComponent(
          payphoneTxId
        )}`;

        const res = await fetch(url, { cache: "no-store" });
        const data = await res.json();

        if (data.status === "approved" && data.id) {
          stopPolling.current = true;
          const successUrl = `/checkout/exito?id=${encodeURIComponent(
            data.id
          )}&clientTransactionId=${encodeURIComponent(payphoneTxId)}`;
          window.location.href = successUrl;
        }
      } catch {}
    };

    const interval = setInterval(() => {
      if (!stopPolling.current) checkStatus();
    }, 2000);

    return () => clearInterval(interval);
  }, [paymentMethod, payphoneTxId]);

  const handlePaymentMethodChange = (e) => {
    const v = e.target.value;
    if (v === "payphone") {
      stopPolling.current = false;
      setPayphoneTxId(newPayphoneTxId());
    }
    setPaymentMethod(v);
  };

  const handleAceptarChange = (e) => {
    const { checked } = e.target;
    setUserData((prev) => ({
      ...prev,
      aceptar: checked
    }));
  };

  const handleShippingChange = (e) => {
    const { value } = e.target;
    if (value === "local") {
      setShipping({ metodo: value, ciudad: "", direccion: "", costo: 0 });
    }
    if (value === "santaelena") {
      setShipping({ metodo: value, ciudad: "Santa Elena", direccion: "", costo: 5 });
    }
    if (value === "provincia") {
      setShipping({ metodo: value, ciudad: "", direccion: "", costo: 8 });
    }
  };

  const handleFieldShipping = (e) => {
    const { name, value } = e.target;
    setShipping((prev) => ({ ...prev, [name]: value }));
  };

  const validShipping =
    shipping.metodo === "local" ||
    (shipping.metodo === "santaelena" && shipping.direccion.trim() !== "") ||
    (shipping.metodo === "provincia" &&
      shipping.direccion.trim() !== "" &&
      shipping.ciudad.trim() !== "");

  const canPay =
    userData.nombre.trim() !== "" &&
    userData.telefono.trim() !== "" &&
    userData.aceptar &&
    validShipping &&
    shipping.metodo !== "";

  const subtotalDisplay = calcSubtotal().toFixed(2);
  const totalDisplay = (calcSubtotal() + shipping.costo).toFixed(2);

  return (
    <>
      <div className="select-group">
        <label>Método de entrega *</label>
        <RadioGroup value={shipping.metodo} onChange={handleShippingChange}>
          <FormControlLabel value="local" control={<Radio />} label="Retiro en local (Gratis)" />
          <FormControlLabel
            value="santaelena"
            control={<Radio />}
            label="Entrega dentro de la ciudad Santa Elena (+ $5)"
          />
          <FormControlLabel
            value="provincia"
            control={<Radio />}
            label="Entrega a otra ciudad de la provincia Santa Elena (+ $8)"
          />
        </RadioGroup>
      </div>

      {shipping.metodo === "santaelena" && (
        <div className="select-group">
          <label htmlFor="direccion">Dirección de entrega *</label>
          <input
            id="direccion"
            name="direccion"
            type="text"
            value={shipping.direccion}
            onChange={handleFieldShipping}
          />
        </div>
      )}

      {shipping.metodo === "provincia" && (
        <>
          <div className="select-group">
            <label htmlFor="ciudad">Ciudad destino *</label>
            <input
              id="ciudad"
              name="ciudad"
              type="text"
              value={shipping.ciudad}
              onChange={handleFieldShipping}
            />
          </div>

          <div className="select-group">
            <label htmlFor="direccion">Dirección de entrega *</label>
            <input
              id="direccion"
              name="direccion"
              type="text"
              value={shipping.direccion}
              onChange={handleFieldShipping}
            />
          </div>
        </>
      )}

      <Box className="d-flex justify-content-between mb-3" style={{ marginTop: "1rem" }}>
        <Typography sx={{ fontWeight: 700 }}>Subtotal</Typography>
        <Typography sx={{ fontWeight: 700 }}>${subtotalDisplay}</Typography>
      </Box>

      <Box className="d-flex justify-content-between mb-3">
        <Typography sx={{ fontWeight: 700 }}>Envío</Typography>
        <Typography sx={{ fontWeight: 700 }}>${shipping.costo.toFixed(2)}</Typography>
      </Box>

      <Box className="d-flex justify-content-between mb-3">
        <Typography sx={{ fontWeight: 700 }}>Total</Typography>
        <Typography sx={{ fontWeight: 700 }}>${totalDisplay}</Typography>
      </Box>

      <div className="privacidad-info" style={{ marginTop: "1rem" }}>
        <FormControlLabel
          control={<Checkbox checked={userData.aceptar} onChange={handleAceptarChange} />}
          label={
            <span>
              * He leído y estoy de acuerdo con los{" "}
              <a href="/terminos-condiciones" target="_blank">términos y condiciones</a> y la{" "}
              <a href="/politicas-privacidad" target="_blank">política de privacidad</a>.
            </span>
          }
        />
      </div>

      <FormControl sx={{ mt: 2, width: "100%" }}>
        <Typography sx={{ fontWeight: 600, mb: 1 }}>Selecciona método de pago</Typography>
        <RadioGroup value={paymentMethod} onChange={handlePaymentMethodChange}>
          <FormControlLabel value="paypal" control={<Radio />} label="PayPal" disabled={!canPay} />
          <FormControlLabel value="payphone" control={<Radio />} label="PayPhone" disabled={!canPay} />
        </RadioGroup>
      </FormControl>

      {!canPay && (
        <Typography sx={{ color: "red", fontSize: "0.9rem", mt: 1 }}>
          Complete todos los datos requeridos para continuar con el pago.
        </Typography>
      )}

      {paymentMethod === "paypal" && canPay && (
        <PaypalBox
          cartItems={cartItems.map((it) => ({
            ...it,
            price: Number(it.price_with_iva ?? it.price ?? 0)
          }))}
          setCartItems={setCartItems}
          txId={`PAYPAL-${Date.now()}`}
        />
      )}

      {paymentMethod === "payphone" && payphoneTxId && canPay && (
        <PayphoneBox
          key={payphoneTxId}
          txId={payphoneTxId}
          amountInCents={amountInCents}
          items={cartItems}
        />
      )}
    </>
  );
}
